/**
 * Footer buttons a step's popover may offer. Structurally identical to driver.js's
 * `AllowedButtons` so the web renderer can pass the array straight through, but
 * declared here so the base lib carries no driver.js (and therefore no DOM)
 * dependency.
 */
export type WalkthroughButton = 'next' | 'previous' | 'close';

/**
 * The set of popover footer buttons to show for a step. Previous is hidden on the
 * first step; Next shows when the step wants it OR it's the last step (the last
 * step's Next reads "End" and finishes); Close (the X) always shows. Pure so the
 * button logic is unit-tested without a renderer.
 */
export function walkthroughShowButtons(step: {
  isFirst: boolean;
  isLast: boolean;
  showNext: boolean;
}): WalkthroughButton[] {
  const buttons: WalkthroughButton[] = [];
  if (step.isLast || step.showNext) {
    buttons.push('next');
  }
  if (!step.isFirst) {
    buttons.push('previous');
  }
  buttons.push('close');
  return buttons;
}

/** "n/total" progress label. driver.js does not substitute {{current}}/{{total}}
 * in single-element highlight mode, so the fully-formed string is passed in. */
export function walkthroughProgressText(index: number, total: number): string {
  return `${index + 1}/${total}`;
}

/** Next-button label: "End" on the final step, otherwise "Next". */
export function walkthroughNextText(isLast: boolean): string {
  return isLast ? 'End' : 'Next';
}

/**
 * Author body text reduced to plain text: markup stripped, entity whitespace
 * collapsed. The NativeScript renderer draws step bodies as text rather than
 * HTML — `WalkthroughController` is `[AllowAnonymous]`, so adding a second
 * platform's HTML renderer over author-supplied bodies would widen the stored-XSS
 * surface tracked by ADO #29555.
 */
export function walkthroughPlainText(html: string | null | undefined): string {
  return (html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
}
